import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getDataList } from '../../api/common';
import { OT_MOVIMIENTOS_KEY } from '../../hooks/useOtMovimientos';
import { VISITA_VIGENTE_KEY } from '../../hooks/useVisitaVigente';

export const OT_DATA_KEY = (id) => ['programacion', id];

export default function useOtLogic({ id }) {
  const queryClient = useQueryClient();

  // modalCharge local: solo para operaciones de mutación (Finalizar, Cancelar, etc.)
  // El spinner de carga inicial viene de isLoading de RQ.
  const [modalCharge, setModalCharge] = useState(false);

  // Estado derivado local — permite que los modales hijos hagan actualizaciones
  // optimistas vía setEquiposOtIni/setSedes/setOt sin perder compatibilidad.
  // El useEffect de abajo los sincroniza cada vez que RQ trae datos frescos.
  const [ots, setOt] = useState(null);
  const [equiposOtIni, setEquiposOtIni] = useState([]);
  const [sedes, setSedes] = useState([]);

  const {
    data: programacionData,
    isLoading: isQuerying,
    error: queryError,
  } = useQuery({
    queryKey: OT_DATA_KEY(id),
    queryFn: async () => {
      const res = await getDataList('Programacion', {
        conditions: { _id: id },
        fields: null,
        options: null,
      });
      const data = res?.data?.data?.[0];
      if (!data) throw new Error('No se encontraron datos de la programación');
      // Normalizar cliente para que Cabecera y demás lo encuentren en data.cliente
      data.cliente = data.clientInformation;
      return data;
    },
    staleTime: 2 * 60 * 1000,   // 2 min — datos frescos tras mutaciones; cache en navegación
    enabled: !!id,
  });

  // Sincronizar estado derivado cuando llegan datos nuevos de RQ.
  // Sobreescribe cualquier update optimista con el valor real del servidor.
  useEffect(() => {
    if (!programacionData) return;
    const equipos = programacionData.equipos || [];
    setOt({
      _id: programacionData.ot,
      numeroOrden: programacionData.numeroOrden || '',
      visitas: programacionData.visitas || 1,
    });
    setEquiposOtIni(equipos.filter(eq => !eq.fechaCalibracion));
    setSedes([...new Set(equipos.map(eq => eq.equipo?.sedeClient).filter(Boolean))]);
  }, [programacionData]);

  // Invalidar las 3 queries tras cualquier mutación — RQ refetcha en background
  // solo las que están montadas en ese momento.
  const toggleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: OT_DATA_KEY(id) });
    // Invalidar movimientos y visita con el otId/programacionId actuales
    if (ots?._id) {
      queryClient.invalidateQueries({ queryKey: OT_MOVIMIENTOS_KEY(ots._id) });
    }
    queryClient.invalidateQueries({ queryKey: VISITA_VIGENTE_KEY(id) });
  }, [queryClient, id, ots?._id]);

  return {
    ots,
    setOt,
    equiposOtIni,
    setEquiposOtIni,
    sedes,
    setSedes,
    // Combinar: spinner de mutación OR carga inicial de RQ
    modalCharge: modalCharge || isQuerying,
    setModalCharge,
    toggleRefresh,
    dataProgramacion: programacionData || null,
    error: queryError?.message || null,
  };
}
